/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { ProductNote, ProductOrders } from "../../../../shared/services/interface";
import InputBox from "../../components/InputBox";
import { notifyError, notifyWarning } from "../../../../assets/toastService";
import BarcodeScanner from "src/shared/components/barcode/BarcodeScanner";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import DropdownSelect from "../../components/DropdownSelect";
import { enableStock } from "./ProductDisplay";
import { useUser } from "../../components/UserContext";
import NumberPad from "src/business-types/grocery/layouts/Home/NumberPad";

export const sellingType = [
  { _id: "1", CategoryName: "Retail" },
  { _id: "2", CategoryName: "Wholesale" },
];

export interface Product {
  productId: string;
  productName: string;
  arabicName: string;
  amount: string | number;
  quantity: number;
  notes: ProductNote[];
  barcodeId: string;
  unitConfiguration: {
    baseUnitType: string;
    stock: number;
    uom: {
      uomName: string;
      baseUnit: string;
      conversionFactor: number | string;
      id: string;
    }[];
  };
  productRefId: string;
  qty: string;
  salesQtyType: {
    uomName: string;
    baseUnit: string;
    conversionFactor: number | string;
    id: string;
  };
  uom: string;
  wholesalePrice?: number;
}

// ── V2 row shape (single source of truth) ──────────────────────────────────
export interface ProductRowV2 {
  productId: string;
  productName: string;
  arabicName: string;
  amount: string | number; // base unit selling price — never changes after select
  qty: string;
  price: string | number; // effective unit price after UOM conversion
  uom: string; // selected UOM id
  typesUOM: { _id: string; CategoryName: string }[];
  stock: number;
  notes: ProductNote[];
  visibleNotes: boolean;
  productRefId: string;
  unitConfiguration: {
    baseUnitType: string;
    stock: number;
    uom: { uomName: string; baseUnit: string; conversionFactor: number | string; id: string }[];
  };
  arabicword?: string;
  sellingType?: string;
  wholesalePrice?: number;
}

const EMPTY_ROW = (): ProductRowV2 => ({
  productId: "",
  productName: "",
  arabicName: "",
  arabicword: "",
  amount: "",
  qty: "",
  price: "",
  uom: "",
  typesUOM: [],
  stock: 0,
  notes: [],
  visibleNotes: false,
  productRefId: "",
  unitConfiguration: { baseUnitType: "", stock: 0, uom: [] },
  wholesalePrice: 0,
});

function NotesProducts({ product, orderProduct, lastProduct, updateProduct, viewProduct, closeProduct, clearData, getUOM }: ProductOrders) {
  const [productDetailsV2, setProductDetailsV2] = useState<ProductRowV2[]>([EMPTY_ROW()]);
  const [fullView, setFullview] = useState<boolean>(false);
  const [productSuggestions, setProductSuggestions] = useState<[string, string][][]>([[]]);

  // FIX: findId tracks only the typed search text per row (never the stored productRefId)
  const [findId, setFindId] = useState<string[]>([""]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const nameRefs = useRef<(HTMLInputElement | null)[]>([]);
  const qtyRefs = useRef<(HTMLInputElement | null)[]>([]);
  const productIdRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { userConfigration } = useUser();
  const [noteText, setNoteText] = useState<string[]>([""]);
  const [noteAmount, setNoteAmount] = useState<number[]>([0]);
  const noteTextRefs = useRef<(HTMLInputElement | null)[]>([]);
  const noteAmountRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showPad, setShowPad] = useState(false);
  const [activeField, setActiveField] = useState<"qty" | "price" | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [editingNote, setEditingNote] = useState<{ productIndex: number; noteIndex: number } | null>(null);
  const [sellingMode, setSellingMode] = useState<"retail" | "wholesale" | "customized">("retail");
  const MAX_NOTES = 8;

  const handleGlobalSellingType = (mode: "retail" | "wholesale" | "customized") => {
    setSellingMode(mode);

    const value = (mode === "retail" || mode === "customized") ? "1" : "2";

    setProductDetailsV2((prev) => {
      let rows = [...prev];

      rows.forEach((row, index) => {
        if (row.productId) {
          rows = applyPriceChanges(rows, index, value);
        }
      });

      return rows;
    });
  };

  // ── Keep parallel arrays in sync whenever rows are added/removed ──────────
  useEffect(() => {
    const len = productDetailsV2.length;
    setNoteText((prev) => (prev.length < len ? [...prev, ...Array(len - prev.length).fill("")] : prev));
    setNoteAmount((prev) => (prev.length < len ? [...prev, ...Array(len - prev.length).fill(0)] : prev));
    setFindId((prev) => (prev.length < len ? [...prev, ...Array(len - prev.length).fill("")] : prev));
  }, [productDetailsV2.length]);

  useEffect(() => {
    if (userConfigration?.isDefaultTabsEnabled) {
      setFullview(Boolean(userConfigration.isDefaultTabsEnabled));
    }
  }, []);

  // ── Notes helpers ──────────────────────────────────────────────────────────
  const handleAddNote = (index: number, note: ProductNote) => {
    setProductDetailsV2((prev) => {
      const updated = prev.map((r) => ({ ...r }));
      const currentNotes = [...(updated[index].notes || [])];

      if (editingNote && editingNote.productIndex === index) {
        currentNotes[editingNote.noteIndex] = note;
        updated[index] = { ...updated[index], notes: currentNotes };
        setEditingNote(null);
        return updated;
      }

      if (currentNotes.length >= MAX_NOTES) {
        notifyWarning("Maximum 8 notes allowed per product");
        return prev;
      }

      updated[index] = { ...updated[index], notes: [...currentNotes, note] };
      return updated;
    });
  };

  const handleDeleteNote = (productIndex: number, noteIndex: number) => {
    setProductDetailsV2((prev) => {
      const updated = prev.map((r) => ({ ...r }));
      const notes = [...(updated[productIndex].notes || [])];
      notes.splice(noteIndex, 1);
      updated[productIndex] = { ...updated[productIndex], notes };
      return updated;
    });
  };

  const handleEditNote = (productIndex: number, noteIndex: number) => {
    const note = productDetailsV2[productIndex].notes?.[noteIndex];
    if (!note) return;

    setNoteText((prev) => {
      const t = [...prev];
      t[productIndex] = note.text;
      return t;
    });
    setNoteAmount((prev) => {
      const a = [...prev];
      a[productIndex] = note.amount;
      return a;
    });
    setEditingNote({ productIndex, noteIndex });
    setTimeout(() => noteTextRefs.current[productIndex]?.focus(), 100);
  };

  // ── UOM price calculation ─────────────────────────────────────────────────
  // price = baseAmount * conversionFactor
  // Called INSIDE setProductDetailsV2 functional updater — receives latest `prev` snapshot.
  // `allUOM` is passed in explicitly so no closure over stale `getUOM`.
  const applyUomChange = (rows: ProductRowV2[], index: number, uomId: string, allUOM: typeof getUOM): ProductRowV2[] => {
    const updated = rows.map((r) => ({ ...r }));
    const row = { ...updated[index] };

    if (!uomId || uomId === "-") {
      row.uom = "";
      row.price = String(row.amount);
      updated[index] = row;
      return updated;
    }

    // Look up inside the product's own uom list first, fall back to global getUOM
    const selectedUOM = allUOM.filter((u: any) => u.id === uomId);
    const baseunitType = allUOM.filter((u: any) => u.id === row.unitConfiguration.baseUnitType);
    const factor = Number(selectedUOM[0]?.conversionFactor ?? 1);
    const wholesalePrice = product.find((p) => p._id.$oid === row.productId)?.wholeSalePrice || 0;
    const newAmount = (row.sellingType === "1" || sellingMode === "retail") ? row.amount : wholesalePrice;
    const baseAmount = Number(newAmount) / Number(baseunitType[0]?.conversionFactor) || 0;
    const newPrice = baseAmount * (factor || 1);

    const rawStock = row.unitConfiguration.stock;
    const stockInUOM = factor > 0 ? rawStock / factor : rawStock;
    const currentQty = Number(row.qty) || 0;
    const safeQty = enableStock && currentQty > stockInUOM ? stockInUOM : currentQty;

    row.uom = uomId;
    row.price = newPrice.toFixed(2);
    row.qty = safeQty.toString();
    updated[index] = row;
    return updated;
  };

  const applyPriceChanges = (rows: ProductRowV2[], index: number, value: string): ProductRowV2[] => {
    const row = { ...rows[index] };

    const selectedUOM = getUOM.find((u: any) => u.id === row.uom);
    const baseunitType = getUOM.find((u: any) => u.id === row.unitConfiguration.baseUnitType);

    const factor = Number(selectedUOM?.conversionFactor ?? 1);
    const baseConversion = Number(baseunitType?.conversionFactor ?? 1); // ← safe fallback
    const wholesalePrice = product.find((p) => p._id.$oid === row.productId)?.wholeSalePrice || 0;

    const baseAmount = value === "1" ? row.amount : wholesalePrice;
    const newPrice = baseAmount && factor > 0 ? (Number(baseAmount) / baseConversion) * factor : 0;

    const rawStock = row.unitConfiguration.stock;
    const stockInUOM = factor > 0 ? rawStock / factor : rawStock;
    const currentQty = Number(row.qty) || 0;
    const safeQty = enableStock && currentQty > stockInUOM ? stockInUOM : currentQty;

    row.price = newPrice.toFixed(2);
    row.qty = safeQty.toString(); // ← FIX: was missing
    row.sellingType = value; // ← keep dropdown in sync

    const updated = [...rows];
    updated[index] = row;
    return updated;
  };

  // ── Numeric validator ──────────────────────────────────────────────────────
  const isValidNumeric = (v: string) => v === "" || /^(0|[1-9]\d*)(\.\d{0,2})?$/.test(v);

  // ── Main change handler ────────────────────────────────────────────────────
  const handleChange = (index: number, key: keyof ProductRowV2, value: string, id?: string) => {
    if (key === "qty") {
      if (value === "" || Number(value) === 0) {
        handleDeleteProduct(index);
        return;
      }
      if (!isValidNumeric(value)) return;
      if (enableStock) {
        const row = productDetailsV2[index];
        const sel = row.unitConfiguration.uom.find((u) => u.id === row.uom);
        const factor = Number(sel?.conversionFactor ?? 1);
        const stockInUOM = factor > 0 ? row.unitConfiguration.stock / factor : row.unitConfiguration.stock;
        if (Number(value) > stockInUOM) {
          notifyWarning("Quantity exceeds available stock");
          return;
        }
      }
      setProductDetailsV2((prev) => {
        const u = [...prev];
        u[index] = { ...u[index], qty: value };
        return u;
      });
      return;
    }

    if (key === "price") {
      if (!isValidNumeric(value)) return;
      setProductDetailsV2((prev) => {
        const u = [...prev];
        u[index] = { ...u[index], price: value };
        return u;
      });
      return;
    }
    if (key === "uom") {
      const currentUOM = getUOM;
      setProductDetailsV2((prev) => applyUomChange([...prev], index, value, currentUOM));
      return;
    }

    if (key === "sellingType") {
      setProductDetailsV2((prev) => applyPriceChanges([...prev], index, value));
      return;
    }
    if (key === "productId" && id === "other") {
      // Check existing "Other" product
      const existingIndex = productDetailsV2.findIndex((item, idx) => item.productRefId === "other" && idx !== index);

      // If already exists -> increase qty only
      if (existingIndex !== -1) {
        setProductDetailsV2((prev) => {
          const updated = [...prev];

          updated[existingIndex] = {
            ...updated[existingIndex],
            qty: (Number(updated[existingIndex].qty || 0) + 1).toString(),
          };

          // Clear current row
          updated[index] = EMPTY_ROW();

          return updated;
        });

        return;
      }

      // Create new "Other" product
      setProductDetailsV2((prev) => {
        const updated = [...prev];

        updated[index] = {
          ...updated[index],
          productId: "other",
          productRefId: "other",
          productName: "Other",
          arabicName: "آحرون",
          qty: "1",
          price: "0",
          amount: "0",
          uom: "",
          typesUOM: [],
          stock: 0,
          notes: updated[index].notes || [],
          visibleNotes: updated[index].visibleNotes || false,
          unitConfiguration: {
            baseUnitType: "",
            stock: 0,
            uom: [],
          },
        };

        if (index === prev.length - 1) {
          updated.push(EMPTY_ROW());
        }

        return updated;
      });

      setProductSuggestions((prev) => {
        const s = [...prev];
        s[index] = [["Other", "other"]];
        return s;
      });

      setActiveIndex(index);
      setActiveField("price");
      setShowPad(true);

      return;
    }

    if (key === "productId") {
      const matches = product.filter((p) => p._id.$oid === id).map((p) => [p.productName, p._id.$oid] as [string, string]);
      setProductDetailsV2((prev) => {
        const u = [...prev];
        u[index] = { ...u[index], productId: value };
        return u;
      });
      setProductSuggestions((prev) => {
        const s = [...prev];
        s[index] = matches;
        return s;
      });
      return;
    }

    setProductDetailsV2((prev) => {
      const u = [...prev];
      u[index] = { ...u[index], [key]: value };
      return u;
    });
  };

  // ── Select a product into a row ───────────────────────────────────────────
  const handleSelectProduct = (index: number, name: string, id: string, productRefId: string) => {
    const selected = product.find((p) => p._id.$oid === id);
    const existingIndex = productDetailsV2.findIndex((item, idx) => item.productRefId === id && idx !== index);

    if (existingIndex !== -1) {
      setProductDetailsV2((prev) => {
        const u = [...prev];
        u[existingIndex] = { ...u[existingIndex], qty: (Number(u[existingIndex].qty) + 1).toString() };
        u[index] = EMPTY_ROW();
        return u;
      });
      setProductSuggestions((prev) => {
        const s = [...prev];
        s[index] = [];
        return s;
      });
      setFindId((prev) => {
        const f = [...prev];
        f[index] = "";
        return f;
      });
      return;
    }

    const firstUom = selected?.unitConfiguration.uom[0];
    const factor = Number(firstUom?.conversionFactor ?? 1);
    const baseAmount = Number(selected?.amount) || 0;

    // 🟢 FIX: Calculate price based on current selling mode
    const wholesalePrice = Number(selected?.wholeSalePrice || 0);
    const effectivePrice = sellingMode === "wholesale" ? wholesalePrice : baseAmount;
    const unitPrice = factor > 0 ? Number(effectivePrice) * factor : Number(effectivePrice);
  
    // 🟢 FIX: Set selling type based on current mode
    const sellingTypeValue = sellingMode === "wholesale" ? "2" : "1";

    // const unitPrice = factor > 0 ? baseAmount * factor : baseAmount;

    setProductDetailsV2((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        productId: id,
        productName: name,
        arabicName: String(selected?.arabicName || ""),
        arabicword: String(selected?.arabicName || ""),
        qty: "1",
        amount: baseAmount.toString(),
        price: unitPrice.toFixed(2),
        productRefId,
        unitConfiguration: selected?.unitConfiguration || { baseUnitType: "", stock: 0, uom: [] },
        typesUOM: (selected?.unitConfiguration.uom || []).map((u) => ({
          _id: u.id,
          CategoryName: u.uomName,
        })),
        uom: firstUom?.id || "",
        stock: selected?.unitConfiguration.stock || 0,
        notes: updated[index].notes || [],
        visibleNotes: updated[index].visibleNotes || false,
        sellingType: sellingTypeValue, // 🟢 FIX: Set selling type
        wholesalePrice: wholesalePrice,
      };
      if (index === prev.length - 1) updated.push(EMPTY_ROW());
      return updated;
    });

    setProductSuggestions((prev) => {
      const s = [...prev];
      if (index === productDetailsV2.length - 1) s.push([]);
      return s;
    });

    // Clear the ref-id search field after a successful selection
    setFindId((prev) => {
      const f = [...prev];
      f[index] = "";
      return f;
    });
  };

  // ── Name / barcode Enter ───────────────────────────────────────────────────
  const selectProducts = (inputData: string, index: number) => {
    const m = product.find((i) => i.productName === inputData || i.barcodeId === inputData);
    if (!m) return;
    const factor = Number(m.unitConfiguration.uom[0]?.conversionFactor ?? 1);
    const base = Number(m.amount) || 0;
    setProductDetailsV2((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        productId: m._id.$oid,
        productName: m.productName,
        arabicName: m.arabicName,
        arabicword: m.arabicName,
        amount: base.toString(),
        price: (factor > 0 ? base * factor : base).toFixed(2),
        qty: "1",
        typesUOM: (m.unitConfiguration.uom || []).map((u) => ({ _id: u.id, CategoryName: u.uomName })),
        uom: m.unitConfiguration.uom[0]?.id || "",
        stock: m.unitConfiguration.stock || 0,
        unitConfiguration: m.unitConfiguration,
        productRefId: m._id.$oid,
      };
      return updated;
    });
    setTimeout(() => qtyRefs.current[index]?.focus(), 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      selectProducts(productDetailsV2[index].productName, index);
      if (index === productDetailsV2.length - 1) {
        setProductDetailsV2((prev) => [...prev, EMPTY_ROW()]);
        setProductSuggestions((prev) => [...prev, []]);
        setTimeout(() => nameRefs.current[index + 1]?.focus(), 100);
      }
    }
  };

  const handleSearchProductByBarcode = (barcodeId: string) => {
    const index = productDetailsV2.length - 1;
    const filter = product.find((i) => i.barcodeId === barcodeId);
    if (!filter) return;
    if (enableStock && filter.unitConfiguration.stock === 0) {
      notifyWarning("Product Stock is empty");
      return;
    }
    handleSelectProduct(index, filter.productName, filter._id.$oid, filter._id.$oid);
  };

  // ── lastProduct effect ─────────────────────────────────────────────────────
  useEffect(() => {
    const index = productDetailsV2.length - 1;
    if (!lastProduct) return;
    if (lastProduct === "other") {
      handleChange(index, "productId", "Other", "other");
      return;
    }
    const filter = product.find((i) => i._id.$oid === lastProduct);
    if (filter) handleSelectProduct(index, filter.productName, filter._id.$oid, filter._id.$oid);
  }, [orderProduct, lastProduct]);

  // ── Auto-select single suggestion ─────────────────────────────────────────
  useEffect(() => {
    productSuggestions.forEach((suggestions, index) => {
      if (!productDetailsV2[index]) return;
      if (suggestions.length === 1 && productDetailsV2[index].productId !== suggestions[0][1]) {
        const [name, id] = suggestions[0];
        handleSelectProduct(index, name, id, id);
      }
    });
  }, [productSuggestions]);

  // ── Propagate to parent ────────────────────────────────────────────────────
  useEffect(() => {
    updateProduct(productDetailsV2);
  }, [productDetailsV2]);
  useEffect(() => {
    setFullview(false);
  }, [closeProduct]);
  useEffect(() => {
    setProductDetailsV2([EMPTY_ROW()]);
    setProductSuggestions([[]]);
    setFindId([""]);
    setNoteText([""]);
    setNoteAmount([0]);
  }, [clearData]);

  // ── Delete row ─────────────────────────────────────────────────────────────
  const handleDeleteProduct = (index: number) => {
    const splice = <T,>(arr: T[], i: number, fallback: T): T[] => {
      const a = [...arr];
      a.splice(i, 1);
      return a.length === 0 ? [fallback] : a;
    };
    setProductDetailsV2((prev) => splice(prev, index, EMPTY_ROW()));
    setProductSuggestions((prev) => splice(prev, index, []));
    setFindId((prev) => splice(prev, index, ""));
    setNoteText((prev) => splice(prev, index, ""));
    setNoteAmount((prev) => splice(prev, index, 0));
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [productDetailsV2.length]);

  // ── findId input ───────────────────────────────────────────────────────────
  const handleFindIdChange = (index: number, value: string) => {
    setFindId((prev) => {
      const f = [...prev];
      f[index] = value;
      return f;
    });
  };

  // ── Enter on ref-id input ──────────────────────────────────────────────────
  const handleKeyDownFind = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key !== "Enter") return;
    const search = (findId[index] ?? "").trim();
    if (!search) return;

    const matched = product.find((p) => p._id.$oid === search) || product.find((p) => p.productRefId === search);

    if (!matched) {
      notifyError(`No product found with id "${search}"`);
      return;
    }

    handleSelectProduct(index, matched.productName, matched._id.$oid, matched._id.$oid);
  };

  // ── Row total ─────────────────────────────────────────────────────────────
  const getProductTotal = (item: ProductRowV2): number => {
    const line = Number(item.price) * Number(item.qty);
    const notesSum = (item.notes || []).reduce((s: number, n: ProductNote) => s + Number(n.amount || 0), 0);
    return line + notesSum;
  };

  // ── Notes submit ───────────────────────────────────────────────────────────
  const NotesUpdate = (index: number, item: ProductRowV2) => {
    if (!noteText[index]) {
      notifyWarning("Note text required");
      return;
    }
    handleAddNote(index, {
      sNo: `${index + 1}-${(item.notes?.length || 0) + 1}`,
      text: noteText[index],
      amount: noteAmount[index] || 0,
    });
    setNoteText((prev) => {
      const t = [...prev];
      t[index] = "";
      return t;
    });
    setNoteAmount((prev) => {
      const a = [...prev];
      a[index] = 0;
      return a;
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="w-full border border-b border-gray-100 p-1 shadow-inner rounded-lg h-full overflow-hidden">
      <div className="w-full flex items-center px-2">
        {/* Left */}
        <h1 className="text-xl font-semibold">Order Details</h1>

        {/* Right */}
        <div className="ml-auto flex items-center gap-6">
          {/* Retail */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="sellingMode" checked={sellingMode === "retail"} onChange={() => handleGlobalSellingType("retail")} />
            <span>Retail</span>
          </label>

          {/* Wholesale */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="sellingMode" checked={sellingMode === "wholesale"} onChange={() => handleGlobalSellingType("wholesale")} />
            <span>Wholesale</span>
          </label>

          {/* Customized */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="sellingMode" checked={sellingMode === "customized"} onChange={() => handleGlobalSellingType("customized")} />
            <span>Customized</span>
          </label>

          {/* View Products Tab */}
          <p
            className="font-light text-blue-600 hover:underline hover:uppercase cursor-pointer"
            onClick={() => {
              setFullview(!fullView);
              viewProduct(fullView);
            }}
          >
            {fullView ? "Close" : "View"} Products Tab
          </p>
        </div>
      </div>

      <div className={`grid grid-cols-[50px_3fr_50px_1fr_25px] ${ThemeBasedColorHeader()} font-semibold p-2 rounded-t gap-2`}>
        {["#", "Product Details", "Qty", "Total"].map((h, i) => (
          <div key={i} className={`flex items-center justify-${i > 2 ? "end" : "center"}`}>
            {h}
          </div>
        ))}
      </div>

      <div className="h-[86%] overflow-auto border border-gray-200 rounded-md scrollbar-thin scrollbar-thumb-gray-400">
        {productDetailsV2.map((item, index) => {
          const isValidProduct = !!item.productId;

          // FIX: UOM options always come from the row's own unitConfiguration (populated on select)
          const uomOptions = item.unitConfiguration.uom.length > 0 ? item.unitConfiguration.uom.map((u) => ({ _id: u.id, CategoryName: u.uomName })) : [{ _id: "-", CategoryName: "-" }];

          return (
            <div key={index} className={`grid grid-cols-[50px_3fr_50px_1fr_25px] ${ThemeBasedColorMenubar()} font-semibold p-2 rounded-t gap-0.5 my-1`} ref={scrollRef}>
              <p className="text-center">{index + 1}</p>

              {/* Ref-ID + name */}
              <div className="grid grid-cols-[50px_3fr] items-center justify-center cursor-pointer">
                <InputBox
                  // FIX: show only the typed search text, never the stored productRefId
                  value={findId[index] ?? ""}
                  onChange={(e) => handleFindIdChange(index, e.target.value)}
                  className={!isValidProduct ? "border-blue-400 w-20px" : "w-20px"}
                  ref={(el) => (productIdRefs.current[index] = el)}
                  onKeyDown={(e) => handleKeyDownFind(e, index)}
                  disabled={isValidProduct}
                  placeholder="Ref ID"
                />
                <InputBox
                  value={item.productName}
                  onChange={(e) => handleChange(index, "productName", e.target.value)}
                  ref={(el) => (nameRefs.current[index] = el)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  disabled
                />
              </div>

              <div className="hidden">
                <BarcodeScanner onDetected={(code: string) => handleSearchProductByBarcode(code)} />
              </div>

              {/* Qty */}
              <div className="flex">
                <InputBox
                  type="text"
                  value={item.qty}
                  readOnly
                  disabled={!isValidProduct}
                  ref={(el) => (qtyRefs.current[index] = el)}
                  className="text-right"
                  onClick={() => {
                    if (!isValidProduct) return;
                    setActiveIndex(index);
                    setActiveField("qty");
                    setShowPad(true);
                  }}
                />
              </div>

              {/* Total */}
              <div className="flex justify-end font-semibold">{getProductTotal(item).toFixed(2)}</div>

              {/* Delete */}
              {item.productName && (
                <div id="deleted" className="flex text-red-600 -mt-3 ml-3">
                  <p onClick={() => handleDeleteProduct(index)} className="cursor-pointer">
                    ❌
                  </p>
                </div>
              )}

              {/* UOM + price row */}
              {item.productId && (
                <div className="col-span-full ml-6 mr-12 mt-0.5 p-1 bg-gray-50 border rounded-lg">
                  <div className="flex gap-4">
                    {/* Notes toggle */}
                    <div
                      className="flex justify-center items-center h-full cursor-pointer"
                      onClick={() =>
                        setProductDetailsV2((prev) => {
                          const u = [...prev];
                          u[index] = { ...u[index], visibleNotes: !item.visibleNotes };
                          return u;
                        })
                      }
                    >
                      {item.visibleNotes ? "⏏️" : "🔽"}
                    </div>

                    {/* UOM dropdown — FIX: sourced from item.unitConfiguration.uom */}
                    <div className="w-[40%] flex flex-col gap-2">
                      <div className="grid grid-cols-[1fr_3fr]">
                        <div className="h-8 w-fit flex gap-2">
                          <div className="mt-2 mr-3">UOM:</div>
                          <div className="w-[120px]">
                            {item.productName === "Other" ? (
                              ""
                            ) : (
                              <DropdownSelect
                                name="uom-select"
                                value={item.uom}
                                options={uomOptions}
                                defultSelect={uomOptions[0]?._id !== "-"}
                                onChange={(val) => {
                                  if (!val || val === "-") return;
                                  handleChange(index, "uom", val);
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {sellingMode === "customized" && (
                        <div className="grid grid-cols-[1fr_3fr] items-center">
                          <div className="flex items-center gap-3">
                            <span className="font-medium">Selling:</span>

                            {item.productName !== "Other" && (
                              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                                {/* Retail Button */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleChange(
                                      index,
                                      "sellingType",
                                      (item.sellingType === "" || item.sellingType === "1") ? "2" : "1"
                                    )
                                  }
                                  className={`px-5 py-2 rounded-lg text-white font-medium transition-all duration-200 ${item.sellingType === "1"
                                    ? "bg-green-600 hover:bg-green-700"
                                    : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                                >
                                  {item.sellingType === "1" ? "Retail" : "Wholesale"}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Unit price */}
                    <div className="grid grid-cols-[2fr_1fr] w-[60%] gap-2">
                      <div className="flex w-fill justify-end items-center">Unit Price :</div>
                      <InputBox
                        type="text"
                        value={item.price}
                        readOnly
                        disabled={!isValidProduct}
                        className="text-right"
                        onClick={() => {
                          if (!isValidProduct) return;
                          setActiveIndex(index);
                          setActiveField("price");
                          setShowPad(true);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Number pad */}
              {showPad && activeIndex !== null && activeField && (
                <NumberPad
                  value={String(productDetailsV2[activeIndex][activeField] || "")}
                  onChange={(val) => handleChange(activeIndex, activeField, val)}
                  onClose={() => {
                    setShowPad(false);
                    setActiveField(null);
                  }}
                />
              )}

              {/* Notes panel */}
              {item.productId && item.visibleNotes && (
                <div className="col-span-full ml-6 mr-12 p-1 bg-gray-50 border rounded-lg">
                  {item.notes.length > 0 ? (
                    item.notes.map((n, i) => (
                      <div key={n.sNo} className="flex justify-between items-center text-sm text-gray-600 group">
                        <span className="cursor-pointer hover:text-blue-600" onClick={() => handleEditNote(index, i)}>
                          {i + 1}. {n.text}
                        </span>
                        <div className="flex items-center gap-3">
                          <span>{Number(n.amount).toFixed(2)}</span>
                          <span className="cursor-pointer text-red-500 hover:text-red-700" onClick={() => handleDeleteNote(index, i)}>
                            ✕
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="w-full flex justify-center items-center text-gray-400 font-light">No data available</p>
                  )}

                  <hr className="my-2" />

                  <div className="mt-3 grid grid-cols-[3fr_1fr_1fr] gap-2">
                    <InputBox
                      placeholder="Alternation Details"
                      value={noteText[index] || ""}
                      ref={(el) => (noteTextRefs.current[index] = el)}
                      onChange={(e) =>
                        setNoteText((prev) => {
                          const t = [...prev];
                          t[index] = e.target.value;
                          return t;
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          noteAmountRefs.current[index]?.focus();
                        }
                      }}
                    />
                    <InputBox
                      type="number"
                      placeholder="Amount"
                      value={noteAmount[index] || ""}
                      ref={(el) => (noteAmountRefs.current[index] = el)}
                      min={0}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "") {
                          setNoteAmount((prev) => {
                            const a = [...prev];
                            a[index] = 0;
                            return a;
                          });
                          return;
                        }
                        if (!/^(0|[1-9]\d*)(\.\d{0,2})?$/.test(val)) return;
                        setNoteAmount((prev) => {
                          const a = [...prev];
                          a[index] = Number(val);
                          return a;
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          NotesUpdate(index, item);
                          noteTextRefs.current[index]?.focus();
                        }
                      }}
                    />
                    <button className="bg-blue-600 text-white rounded-md px-4 py-2" onClick={() => NotesUpdate(index, item)}>
                      +Alt
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default NotesProducts;
